import { showErrorToast } from 'components/utils/util'
import { isValid, parse, set } from 'date-fns'
import Papa from 'papaparse'
import { v4 } from 'uuid'
import { ShiftRowSchema } from '../bulkCreateShiftsSchema'

enum HeaderEnum {
  CompanyId = 'company: id',
  LocationName = 'location: name',
  LocationArrivalInstructions = 'location: arrival instructions',
  LocationStreet = 'location: street',
  LocationUnit = 'location: unit',
  LocationCity = 'location: city',
  LocationState = 'location: state',
  LocationZip = 'location: zip',
  PositionId = 'position: id',
  ShiftStartDate = 'shift: start date',
  ShiftStartTime = 'shift: start time',
  ShiftEndDate = 'shift: end date',
  ShiftEndTime = 'shift: end time',
  ShiftWorkersNeeded = 'shift: workers needed',
  ShiftLunchLength = 'shift: lunch length',
  ShiftPayRate = 'shift: pay rate',
  ShiftPayLumpSum = 'shift: pay lump sum',
  ShiftBundleGroup = 'shift: bundle group',
  SupervisorName = 'supervisor: name',
  SupervisorPhone = 'supervisor: phone',
  WorkerIds = 'shift: worker ids',
}

const REQUIRED_HEADERS = [
  HeaderEnum.CompanyId,
  HeaderEnum.LocationName,
  HeaderEnum.LocationArrivalInstructions,
  HeaderEnum.LocationStreet,
  HeaderEnum.LocationUnit,
  HeaderEnum.LocationCity,
  HeaderEnum.LocationState,
  HeaderEnum.LocationZip,
  HeaderEnum.PositionId,
  HeaderEnum.ShiftStartDate,
  HeaderEnum.ShiftStartTime,
  HeaderEnum.ShiftEndDate,
  HeaderEnum.ShiftEndTime,
  HeaderEnum.ShiftWorkersNeeded,
  HeaderEnum.ShiftLunchLength,
  HeaderEnum.ShiftPayRate,
  HeaderEnum.ShiftPayLumpSum,
  HeaderEnum.ShiftBundleGroup,
  HeaderEnum.SupervisorName,
  HeaderEnum.SupervisorPhone,
]
const OPTIONAL_HEADERS = [HeaderEnum.WorkerIds]
const ALL_HEADERS = [...REQUIRED_HEADERS, ...OPTIONAL_HEADERS]

const ACCEPTED_DATE_FORMATS = [
  'MM/dd/yy',
  'MM/dd/yyyy',
  'dd/MM/yy',
  'dd/MM/yyyy',
  'yyyy-MM-dd',
]

const ACCEPTED_TIME_FORMATS = [
  'HH:mm',
  'HH:mm:ss',
  'hh:mm',
  'hh:mm:ss',
  'hh:mm a',
  'hh:mm:ss a',
]

export const parseBulkCreateShiftDataFromClipboard = async (
  data: string
): Promise<ShiftRowSchema[]> => {
  const parseResult: { data: string[][] } = await new Promise((resolve) => {
    Papa.parse(data, {
      header: false,
      complete: (results) => {
        resolve(results)
      },
    })
  })

  const rows = parseResult.data.filter((row) =>
    row.some((cell) => cell.length > 0)
  )

  return parseRows(rows)
}

export const parseBulkCreateShiftDataFromCSVFile = async (
  data: string
): Promise<ShiftRowSchema[]> => {
  const parseResult: { data: string[][] } = await new Promise((resolve) => {
    Papa.parse(data, {
      header: false,
      complete: (results) => {
        resolve(results)
      },
    })
  })

  const rows = parseResult.data.filter((row) =>
    row.some((cell) => cell.length > 0)
  )

  return parseRows(rows)
}

function parseRows(rows: string[][]): ShiftRowSchema[] {
  if (rows.length < 2) {
    return []
  }

  const headersRow = rows[0].map((header) => header.toLowerCase())

  if (
    headersRow.length < REQUIRED_HEADERS.length ||
    REQUIRED_HEADERS.some(
      (header) => !headersRow.includes(header as HeaderEnum)
    )
  ) {
    // Missing required headers
    const message = REQUIRED_HEADERS.filter(
      (header) => !headersRow.includes(header as HeaderEnum)
    ).join(', ')
    console.error(`Missing required headers: ${message}`)
    showErrorToast(message)
  }

  const hasUnknownHeaders = headersRow.some(
    (header) => !ALL_HEADERS.includes(header as HeaderEnum)
  )

  if (hasUnknownHeaders) {
    // Unknown headers
    const message = headersRow
      .filter((header) => !ALL_HEADERS.includes(header as HeaderEnum))
      .join(', ')
    console.error('Unknown headers', message)
    showErrorToast(`Unknown headers: ${message}`)
  }

  const headersPositionsMap = ALL_HEADERS.reduce(
    (acc, header) => {
      const foundIndex = headersRow.indexOf(header)
      if (foundIndex === -1) {
        return acc
      }

      return {
        ...acc,
        [header]: foundIndex,
      }
    },
    {} as Record<HeaderEnum, number>
  )

  const dataRows = rows.slice(1)
  return dataRows.map((row) => parseEntryRow(row, headersPositionsMap))
}

function parseEntryRow(
  row: string[],
  headersPositionsMap: Record<HeaderEnum, number>
): ShiftRowSchema {
  const companyId = parseInt(row[headersPositionsMap[HeaderEnum.CompanyId]])
  const positionId = parseInt(row[headersPositionsMap[HeaderEnum.PositionId]])

  // Flexible date parsing for shift start date and time
  const parsedShiftStartDate = tryParseFlexibleDate(
    row[headersPositionsMap[HeaderEnum.ShiftStartDate]]
  )
  const parsedShiftStartTime = tryParseFlexibleTime(
    row[headersPositionsMap[HeaderEnum.ShiftStartTime]]
  )
  const shiftStartDate = set(parsedShiftStartDate ?? new Date(), {
    hours: parsedShiftStartTime?.getHours() ?? 0,
    minutes: parsedShiftStartTime?.getMinutes() ?? 0,
  })

  // Flexible date parsing for shift end date and time
  const parsedShiftEndDate = tryParseFlexibleDate(
    row[headersPositionsMap[HeaderEnum.ShiftEndDate]]
  )
  const parsedShiftEndTime = tryParseFlexibleTime(
    row[headersPositionsMap[HeaderEnum.ShiftEndTime]]
  )
  const shiftEndDate = set(parsedShiftEndDate ?? new Date(), {
    hours: parsedShiftEndTime?.getHours() ?? 0,
    minutes: parsedShiftEndTime?.getMinutes() ?? 0,
  })

  const parsedShiftWorkersNeeded = parseInt(
    row[headersPositionsMap[HeaderEnum.ShiftWorkersNeeded]]
  )
  const shiftWorkersNeeded = Number.isNaN(parsedShiftWorkersNeeded)
    ? 0
    : parsedShiftWorkersNeeded
  const parsedShiftLunchLength = parseInt(
    row[headersPositionsMap[HeaderEnum.ShiftLunchLength]]
  )
  const shiftLunchLength = Number.isNaN(parsedShiftLunchLength)
    ? 0
    : parsedShiftLunchLength
  const parsedShiftPayRate = tryParseCurrency(
    row[headersPositionsMap[HeaderEnum.ShiftPayRate]]
  )
  const shiftPayRate =
    !parsedShiftPayRate || Number.isNaN(parsedShiftPayRate)
      ? null
      : parsedShiftPayRate
  const parsedShiftPayLumpSum = tryParseCurrency(
    row[headersPositionsMap[HeaderEnum.ShiftPayLumpSum]]
  )
  const shiftPayLumpSum =
    !parsedShiftPayLumpSum || Number.isNaN(parsedShiftPayLumpSum)
      ? null
      : parsedShiftPayLumpSum

  const parsedWorkerIds = row[headersPositionsMap[HeaderEnum.WorkerIds]]
    ? row[headersPositionsMap[HeaderEnum.WorkerIds]]
        ?.split(',')
        ?.map((id) => parseInt(id.trim()))
        ?.filter((id) => !Number.isNaN(id))
    : undefined

  return {
    rowId: v4(),
    companyId: Number.isNaN(companyId) ? 0 : companyId,
    locationName: row[headersPositionsMap[HeaderEnum.LocationName]],
    locationArrivalInstructions:
      row[headersPositionsMap[HeaderEnum.LocationArrivalInstructions]],
    locationStreet: row[headersPositionsMap[HeaderEnum.LocationStreet]],
    locationUnit: row[headersPositionsMap[HeaderEnum.LocationUnit]],
    locationCity: row[headersPositionsMap[HeaderEnum.LocationCity]],
    locationState: row[headersPositionsMap[HeaderEnum.LocationState]],
    locationZip: row[headersPositionsMap[HeaderEnum.LocationZip]],
    positionId: Number.isNaN(positionId) ? 0 : positionId,
    shiftStartDate,
    shiftEndDate,
    shiftWorkersNeeded,
    shiftLunchLength,
    shiftPayRate,
    shiftPayLumpSum,
    shiftBundleGroup: row[headersPositionsMap[HeaderEnum.ShiftBundleGroup]],
    shiftSupervisorName: row[headersPositionsMap[HeaderEnum.SupervisorName]],
    shiftSupervisorPhone: row[headersPositionsMap[HeaderEnum.SupervisorPhone]],
    workerIds: parsedWorkerIds,
  } satisfies ShiftRowSchema
}

function tryParseFlexibleDate(dateString: string) {
  if (!dateString) {
    return null
  }

  for (const format of ACCEPTED_DATE_FORMATS) {
    const date = parse(dateString, format, new Date())
    if (isValid(date)) {
      return date
    }
  }

  return null
}

function tryParseFlexibleTime(timeString: string) {
  if (!timeString) {
    return null
  }

  for (const format of ACCEPTED_TIME_FORMATS) {
    const time = parse(timeString, format, new Date())
    if (isValid(time)) {
      return time
    }
  }
  return null
}

function tryParseCurrency(currencyString: string) {
  if (!currencyString) {
    return null
  }

  const parsedCurrency = parseFloat(currencyString.replace(/[^0-9.]/g, ''))
  return Number.isNaN(parsedCurrency) ? null : parsedCurrency
}
